import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ExportSettingsModel } from '@modules/master-data/model/export-settings.model';
import { PaginationResponseModel } from '@base/model/common/pagination-response.model';
import { FileUploadingModel } from '@base/modules/rest/common/model/file-uploading.model';
import { ImportColumnMappingModel } from '@shared/components/dialogs/import-dialog/model/import-settings.model';
import { FileRestUtilsService } from '../common/file-rest-utils.service';
import { AppConfiguration } from '../../../../app-configuration';
import { ObjectDefinitionModel } from '../bbs-object/model/object-definition.model';
import { UserFieldSettingModel } from '../bbs-object/model/user-field-setting.model';
import { ActionResponseModel } from '../model/action-response.model';
import { ObjectFindRequestModel } from './model/object-find-request.model';
import { ObjectActionModel } from './model/object-action.model';
import { ObjectActionRequestModel } from './model/object-action-request.model';
import { ObjectEventRequestModel } from './model/object-event-request.model';
import { RulesExecuteModel } from './model/rules-execute.model';
import { RulesExecutionResultModel } from './model/rules-execution-result.model';
import { RulesFormulaExecutionModel } from './model/rules-formula-execution.model';

export class ObjectRestService<E = any> {
  baseUrl = this.appConfiguration.serverApiUrl + '/secured/objects';

  constructor(private httpClient: HttpClient,
              private appConfiguration: AppConfiguration,
              private objectName: string,
              private fileRestUtilsService: FileRestUtilsService) {
  }

  private url(): string {
    return `${this.baseUrl}/${this.objectName}`;
  }

  getDefinition(): Observable<ObjectDefinitionModel> {
    return this.httpClient.get<ObjectDefinitionModel>(`${this.url()}/definition`);
  }

  find(data: ObjectFindRequestModel): Observable<PaginationResponseModel<E>> {
    return this.httpClient.post<PaginationResponseModel<E>>(`${this.url()}/find`, data);
  }

  findAllByFilter(data: ObjectFindRequestModel): Observable<E[]> {
    return this.httpClient.post<E[]>(`${this.url()}/find-all`, data);
  }

  findAll(): Observable<E[]> {
    return this.httpClient.get<E[]>(`${this.url()}/all`);
  }

  findOneSimple(payload: { id: number; fetchRelations?: string[]; params?: Map<string, object> }): Observable<E> {
    const request = {fetchRelations: payload.fetchRelations || [], params: payload.params ?? new Map<string, object>()};
    return this.httpClient.post<E>(`${this.url()}/${payload.id}/find-one-simple`, request);
  }

  findOne(payload: { id: number; fetchRelations?: string[]; params?: Map<string, object>}): Observable<E> {
    const request = {fetchRelations: payload.fetchRelations || [], params: payload.params ?? new Map<string, object>()};
    return this.httpClient.post<E>(`${this.url()}/${payload.id}/find-one`, request);
  }

  create(payload: { item: E; autoLockOnSave?: boolean }): Observable<E> {
    const request = {main: payload.item, autoLockOnSave: payload.autoLockOnSave};
    return this.httpClient.post<E>(`${this.url()}`, request);
  }

  update(payload: { id: number; item: E }): Observable<E> {
    const request = {main: payload.item};
    return this.httpClient.put<E>(`${this.url()}/${payload.id}`, request);
  }

  accept(payload: { id: number; item: E }): Observable<any> {
    const request = {main: payload.item};
    return this.httpClient.put<any>(`${this.url()}/${payload.id}/accept`, request);
  }

  cancel(payload: { id: number; item: E }): Observable<any> {
    const request = {main: payload.item};
    return this.httpClient.put<any>(`${this.url()}/${payload.id}/cancel`, request);
  }

  delete(payload: { id: number }): Observable<void> {
    return this.httpClient.delete<void>(`${this.url()}/${payload.id}`);
  }

  doAction(data: ObjectActionRequestModel): Observable<ObjectActionModel | any> {
    return this.httpClient.post<ObjectActionModel | any>(`${this.url()}/action`, data);
  }

  doEvent(data: ObjectEventRequestModel): Observable<ActionResponseModel> {
    return this.httpClient.post<ActionResponseModel>(`${this.url()}/event`, data);
  }

  export(data: ExportSettingsModel): Observable<ArrayBuffer> {
    return this.httpClient.post<ArrayBuffer>(`${this.url()}/export`, data, {responseType: 'blob' as 'json'});
  }

  import(importSettings: ImportColumnMappingModel, file: File): Observable<FileUploadingModel<ArrayBuffer>> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('importSettings', new Blob(
      [JSON.stringify(importSettings)],
      {type: 'application/json'}
    ));
    return this.fileRestUtilsService.uploadFileRequest<ArrayBuffer>(`${this.url()}/import`, formData, file.size, 'blob' as 'json');
  }

  saveColumns(payload: { fields: UserFieldSettingModel[]; setForAll: boolean }): Observable<ObjectDefinitionModel> {
    return this.httpClient.post<ObjectDefinitionModel>(`${this.url()}/columns`, payload);
  }

  findEntitiesByPropertyType(propertyType: string): Observable<string[]> {
    return this.httpClient.get<string[]>(`${this.baseUrl}/by-property-type`, {
      params: {propertyType: propertyType},
    });
  }

  executeRules(data: RulesExecuteModel): Observable<RulesExecutionResultModel> {
    return this.httpClient.post<RulesExecutionResultModel>(`${this.url()}/execute-rules`, data);
  }

  executeFormula(data: RulesFormulaExecutionModel): Observable<RulesFormulaExecutionModel> {
    return this.httpClient.post<RulesFormulaExecutionModel>(`${this.url()}/execute-formula`, data);
  }
}
